import React, { useState, useMemo, useEffect } from 'react';
import MedicalDossierSelector from '../../../components/MedicalDossierSelector/MedicalDossierSelector';
import { Project, AssignableReviewer } from '../../../services/AxiosServices';
import { formatSeverity, SEVERITY_OPTIONS, severityToApiValue } from '../../../utils/consguard/severity';
import { updateConsistencyGuardSeverityApi, assignReviewerApi } from '../../../services/AxiosServices';
import { useAuth0 } from '@auth0/auth0-react';
import MultiSelectFilter from '../../../components/consguard/MultiSelectFilter';

interface LeftPanelProps {
  data: any;
  selectedId: string | null;
  onSelectRow: (id: string) => void;
  isCollapsed: boolean;
  onExport: () => void;
  isExporting: boolean;
  selectedProjectId?: string;
  projects: Project[];
  onProjectChange?: (projectId: string) => void;
  onSeverityChange?: (id: string, newSeverity: string) => void;
  onRefreshData?: () => void;
  assignableReviewersData?: AssignableReviewer[];
} 

type SortField = 'id' | 'title' | 'category' | 'severity' | 'reviewer' | 'status' | null;
type SortOrder = 'asc' | 'desc';

const LeftPanel: React.FC<LeftPanelProps> = ({ data, selectedId, onSelectRow, isCollapsed, onExport, isExporting, selectedProjectId, projects, assignableReviewersData, onProjectChange, onSeverityChange, onRefreshData }) => {
  const { getAccessTokenSilently } = useAuth0();

  const reviewersApiOptions = Array.isArray(assignableReviewersData) ? assignableReviewersData : [];
  const reviewerDisplay = (id: string) => reviewersApiOptions.find(r => r.user_id === id)?.nickname || id;
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [localInconsistencies, setLocalInconsistencies] = useState<any[]>([]);
  const [severitySavingId, setSeveritySavingId] = useState<string | null>(null);
  useEffect(() => {
    if (data.inconsistencies) {
      setLocalInconsistencies(data.inconsistencies);
    }
  }, [data.inconsistencies]);

  useEffect(() => {
    setActiveFilters({ category: [], status: [], reviewer: [] });
  }, [data.inconsistencies]);

  const [activeFilters, setActiveFilters] = useState({
    category: [] as string[],
    status: [] as string[],
    reviewer: [] as string[]
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const applyFilters = (category: keyof typeof activeFilters, newChecked: string[]) => {
    setActiveFilters(prev => ({ ...prev, [category]: newChecked }));
  };

  const distinctCategories = useMemo(() => Array.from(new Set(localInconsistencies.map(i => i.category))).filter(Boolean), [localInconsistencies]);
  const distinctStatuses = useMemo(() => Array.from(new Set(localInconsistencies.map(i => i.status))).filter(Boolean), [localInconsistencies]);
  const distinctReviewers = useMemo(() => Array.from(new Set(localInconsistencies.map(i => i.reviewer))).filter(Boolean), [localInconsistencies]);

  const processedData = useMemo(() => {
    let result = [...localInconsistencies];

    if (activeFilters.category.length > 0) {
      result = result.filter(item => activeFilters.category.includes(item.category));
    }
    if (activeFilters.status.length > 0) {
      result = result.filter(item => activeFilters.status.includes(item.status));
    }
    if (activeFilters.reviewer.length > 0) {
      result = result.filter(item => activeFilters.reviewer.includes(item.reviewer));
    }

    if (sortField) {
      result.sort((a, b) => {
        if (sortField === 'id') {
          const strA = (a.id || '').toString();
          const strB = (b.id || '').toString();
          const comp = strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' });
          return sortOrder === 'asc' ? comp : -comp;
        }

        let valA = a[sortField];
        let valB = b[sortField];

        if (sortField === 'severity') {
          valA = (a.severity || "").length;
          valB = (b.severity || "").length;
        } else if (typeof valA === 'string' && typeof valB === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }

        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [localInconsistencies, sortField, sortOrder, activeFilters]);

  const hasApproved = processedData.some(item => item.status === 'Approved');

  const handleExport = () => {
    if (!hasApproved || isExporting) return;
    onExport();
  };

  const handleReviewerChange = async (id: string, newUser_id: string) => {
    setLocalInconsistencies(prev => prev.map(item =>
      item.id === id ? { ...item, reviewer: reviewersApiOptions.find(r => r.user_id === newUser_id)?.nickname || newUser_id } : item
    ));
    const inconsistencyId = localInconsistencies.find(i => i.id === id)?.inconsistency_id;
    if (!inconsistencyId) return;
    try {
      const token = await getAccessTokenSilently();
      await assignReviewerApi(inconsistencyId, newUser_id, token);
      onRefreshData?.();
    } catch (err) {
      console.error('Failed to assign reviewer:', err);
    }
  };

  const handleSeverityChange = async (rowId: string, inconsistencyId: string, newSeverity: string) => {
    if (!rowId || !inconsistencyId || !newSeverity) return;
    setSeveritySavingId(rowId);
    try {
      const token = await getAccessTokenSilently();
      const result = await updateConsistencyGuardSeverityApi<any>(inconsistencyId, severityToApiValue(newSeverity), token);
      if (result) {
        setLocalInconsistencies(prev => prev.map(item =>
          item.inconsistency_id === inconsistencyId ? { ...item, severity: formatSeverity(newSeverity) || newSeverity } : item
        ));
        onSeverityChange?.(inconsistencyId, formatSeverity(newSeverity) || newSeverity);
        onRefreshData?.();
      } else {
        setLocalInconsistencies(prev => prev.map(item =>
          item.id === rowId ? { ...item, severity: '' } : item
        ));
      }
    } catch (err) {
      console.error('Failed to save severity:', String(err));
    } finally {
      setSeveritySavingId(null);
    }
  };

  const getStatusClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'open';
      case 'review': return 'in-review';
      case 'approved': return 'approved';
      case 'archived': return 'archived';
      default: return '';
    }
  };



  const SortArrow = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="sort-arrow">▲</span>;
    return <span className="sort-arrow">{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div className="cg-header">
        <div className="cg-header-top">
          <div className="cg-dossier-info">
            <div className="cg-progress-circle" style={{ '--progress': `${data.dossier?.resolvedPercentage || 0}%` } as any}>
              <div className="cg-progress-inner">{data.dossier?.resolvedPercentage || 0}%</div>
            </div>
            <div className="cg-dossier-details">
              {!isCollapsed && <MedicalDossierSelector 
                selectedProjectId={selectedProjectId} 
                projects={projects} 
                onProjectChange={onProjectChange} 
              />}
              <p>Owner: {data.dossier?.owner}</p>
              <p className="cg-inconsistencies-count">{data.dossier?.totalInconsistencies} inconsistencies</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {!isCollapsed && (
        <div className="cg-filters">
          <button
            className="cg-export-btn"
            onClick={handleExport}
            disabled={!hasApproved || isExporting}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
            {isExporting ? 'Exporting...' : 'Export XLS'}
          </button>
      {/* TODO: reduce code by using a loop here */}
          <div className="cg-filter-dropdown-wrapper">
            <MultiSelectFilter
              label="Category"
              options={distinctCategories}
              checkedValues={activeFilters.category}
              onChange={(checked) => applyFilters('category', checked)}
            />
          </div>

          <div className="cg-filter-dropdown-wrapper">
            <MultiSelectFilter
              label="Status"
              options={distinctStatuses}
              checkedValues={activeFilters.status}
              onChange={(checked) => applyFilters('status', checked)}
            />
          </div>

          <div className="cg-filter-dropdown-wrapper">
            <MultiSelectFilter
              label="Reviewer"
              options={distinctReviewers}
              checkedValues={activeFilters.reviewer}
              onChange={(checked) => applyFilters('reviewer', checked)}
              optionLabel={reviewerDisplay}
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="cg-table-container">
        <table className="cg-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('id')} style={{ width: '5%' }}>ID <SortArrow field="id" /></th>
              <th onClick={() => handleSort('title')} style={{ width: isCollapsed ? '95%' : '30%' }}>Title <SortArrow field="title" /></th>
              {!isCollapsed && (
                <>
                  <th onClick={() => handleSort('category')}>Category <SortArrow field="category" /></th>
                  <th onClick={() => handleSort('severity')}>Severity <SortArrow field="severity" /></th>
                  <th onClick={() => handleSort('reviewer')}>Reviewer <SortArrow field="reviewer" /></th>
                  <th onClick={() => handleSort('status')}>Status <SortArrow field="status" /></th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {processedData.map((row) => (
              <tr
                key={row.id}
                className={selectedId === row.id ? 'selected' : ''}
                onClick={() => onSelectRow(row.id)}
              >
                <td>{row.id}</td>
                <td>{row.title}</td>
                {!isCollapsed && (
                  <>
                    <td>{row.category}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="cg-severity-cell" style={{ position: 'relative', display: 'inline-flex' }}>
                        {severitySavingId === row.id ? (
                          <span
                            className="cg-severity-select"
                            aria-label={`Severity for ${row.id}`}
                            title="Saving..."
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 16,
                              fontWeight: 700,
                              color: '#334155',
                              letterSpacing: 2,
                              cursor: 'wait',
                              padding: '2px 18px 2px 4px',
                            }}
                          >
                            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#334155', animation: 'cg-dots-blink 1.2s infinite' }}>{/* dot 1 */}</span>
                            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#334155', animation: 'cg-dots-blink 1.2s infinite 0.2s' }}>{/* dot 2 */}</span>
                            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#334155', animation: 'cg-dots-blink 1.2s infinite 0.4s' }}>{/* dot 3 */}</span>
                          </span>
                        ) : (
                          <select
                            className="cg-severity-select"
                            aria-label={`Severity for ${row.id}`}
                            title={`Severity: ${formatSeverity(row.severity) || row.severity}`}
                            value={formatSeverity(row.severity) || row.severity || '**'}
                            onChange={(e) => handleSeverityChange(row.id, row.inconsistency_id, e.target.value)}
                            style={{ cursor: 'pointer' }}
                          >
                            {SEVERITY_OPTIONS.map((sev) => (
                              <option key={sev} value={sev}>{sev}</option>
                            ))}
                          </select>
                        )}
                        {!severitySavingId && (
                          <div className="cg-severity-arrow" style={{ position: 'absolute', right: 4, pointerEvents: 'none', display: 'flex' }}>
                            <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
                              <path d="M1 1L4 4L7 1" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="cg-reviewer-cell">
                        <select
                          className="cg-reviewer-select"
                          value={reviewersApiOptions.find(r => r.user_id === row.reviewer)?.user_id || ''}
                          onChange={(e) => handleReviewerChange(row.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">Assign Reviewer</option>
                          {reviewersApiOptions.map((r: AssignableReviewer) => (
                            <option key={r.user_id} value={r.user_id}>{r.nickname}</option>
                          ))}
                        </select>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#94a3b8', position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </div>
                    </td>
                    <td>
                      <span className={`cg-status ${getStatusClass(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeftPanel;
